/**
 * Dashboard overview route (APP-002 §11, §12).
 *
 * Replaces the APP-001 placeholder with a real overview page composed from the
 * backend overview endpoint. Renders only fields the contract actually
 * returns — no fabricated values, no client-side quota math, no inferred
 * "unlimited" from arbitrary numbers. The browser never decides account, plan,
 * quota, entitlement, or billing state (APP-002 §44).
 *
 * Hierarchy (APP-002 §12): heading → alerts → core summary cards → usage
 * snapshot → getting-started → recent activity (empty states) → next action.
 *
 * Loading uses skeletons matching the final layout (APP-002 §20); no full-page
 * spinner after the shell renders. Overview failures degrade to an error state
 * with retry + request ID, never to fake data.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useDashboardIdentity } from '@/layouts/DashboardLayout';
import { getOverview } from '@/api/dashboard';
import { queryKeys } from '@/api/queryKeys';
import { isApiError } from '@/api/errors';
import { track } from '@/lib/analytics';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Key, Sparkles, RefreshCw, Clock } from '@/components/icons';
import { PageContainer } from '@/components/navigation/PageContainer';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Breadcrumbs } from '@/components/navigation/Breadcrumbs';
import { ErrorState } from '@/components/feedback/ErrorState';
import { MetricCard, PlanCard, StatusCard, ActionCard, SummarySection } from '@/components/dashboard/MetricCard';
import { AccountAlerts, deriveAccountAlerts } from '@/components/dashboard/AccountAlerts';
import { GettingStarted } from '@/components/dashboard/GettingStarted';
import { NoApiKeysEmpty, NoBulkJobsEmpty, NoWebhooksEmpty } from '@/components/dashboard/EmptyStates';
import {
  formatCount,
  formatLimit,
  formatRemaining,
  formatDate,
  formatBillingInterval,
  isUnlimited,
  usagePercent,
} from '@/lib/format';
import {
  presentAccountStatus,
  presentEntitlementStatus,
  presentSubscriptionStatus,
} from '@/lib/statusMapping';
import { useEffect } from 'react';

// Growth+ supports bulk and webhooks (PLAN_QUOTA_REFERENCE). Used ONLY as a
// non-authoritative display hint for empty-state copy; the backend remains the
// plan authority and the gating endpoint enforces it.
const BULK_WEBHOOK_PLANS = new Set(['Scale', 'Business', 'Enterprise']);

export function DashboardRoute() {
  const identity = useDashboardIdentity();

  const overview = useQuery({
    queryKey: queryKeys.overview.detail(),
    queryFn: ({ signal }) => getOverview(signal),
    refetchOnWindowFocus: true,
  });

  // Analytics: overview loaded (no sensitive payload). APP-002 §27.
  useEffect(() => {
    if (overview.isSuccess) track({ event: 'dashboard_overview_loaded' });
  }, [overview.isSuccess]);

  const account = identity.account;
  const data = overview.data;
  const loading = overview.isLoading;
  const showOverviewError =
    overview.isError &&
    isApiError(overview.error) &&
    (overview.error.status >= 500 || overview.error.isNetworkError);

  const alerts = data ? deriveAccountAlerts(data) : [];
  const usagePct = data ? usagePercent(data.usage.requests_used, data.usage.requests_limit) : { clamped: 0, raw: 0 };
  const quotaTone =
    usagePct.raw >= 100 ? 'danger' : usagePct.raw >= 80 ? 'warning' : 'default';
  const planName = data?.plan.name ?? null;
  const bulkSupported = planName ? BULK_WEBHOOK_PLANS.has(planName) : false;

  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: 'Overview' }]} />
      <PageHeader
        title="Overview"
        description="Account, plan, and usage summary."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              track({ event: 'dashboard_refresh_requested' });
              void overview.refetch();
            }}
            disabled={overview.isFetching}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      {/* Account/entitlement alerts — prominent only when there is cause. */}
      {data ? <AccountAlerts alerts={alerts} /> : null}

      {/* Core summary cards: current access + current usage. */}
      <SummarySection className="mt-4">
        <PlanCard
          planName={planName}
          billingInterval={data ? formatBillingInterval(data.plan.billing_interval) : undefined}
          loading={loading}
          to="/billing"
          badge={
            data ? (
              <Badge variant={presentEntitlementStatus(data.plan.entitlement_status).variant}>
                {presentEntitlementStatus(data.plan.entitlement_status).label}
              </Badge>
            ) : null
          }
        />
        <MetricCard
          label="Requests used"
          value={data ? formatCount(data.usage.requests_used) : null}
          loading={loading}
          unavailable={!data && !loading}
          progress={
            data
              ? {
                  value: usagePct.clamped,
                  ariaLabel: 'Monthly requests used',
                  tone: quotaTone,
                }
              : undefined
          }
          supporting={
            data ? (
              <>
                {isUnlimited(data.usage.requests_limit)
                  ? 'Unlimited plan'
                  : `${formatRemaining(data.usage.requests_remaining)} of ${formatLimit(
                      data.usage.requests_limit,
                    )} remaining`}
              </>
            ) : null
          }
          warning={quotaTone !== 'default'}
        />
        <MetricCard
          label="Explanations used"
          value={data ? formatCount(data.usage.explanations_used) : null}
          loading={loading}
          unavailable={!data && !loading}
          supporting={
            data ? (
              <>
                {isUnlimited(data.usage.explanations_limit)
                  ? 'Unlimited'
                  : `${formatRemaining(data.usage.explanations_remaining)} remaining`}
              </>
            ) : null
          }
        />
        <MetricCard
          label="Active API keys"
          value={data ? formatCount(data.api_keys.active_count) : null}
          loading={loading}
          unavailable={!data && !loading}
          supporting={data ? <>{data.api_keys.revoked_count} revoked</> : null}
        />
      </SummarySection>

      {/* APP-003: concise link to the full usage page. The overview stays
          short; the usage page owns the full quota-window + history detail. */}
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="link" asChild className="h-auto px-0 text-xs">
          <Link to="/usage">View detailed usage</Link>
        </Button>
      </div>

      {/* Retryable overview error (5xx/network). Never fake data on failure. */}
      {showOverviewError ? (
        <div className="mt-4">
          <ErrorState
            title="Usage details unavailable"
            description="We could not load your account overview. Please try again."
            error={overview.error}
            onRetry={() => void overview.refetch()}
          />
        </div>
      ) : null}

      {/* Usage snapshot + plan/access distinction. */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <StatusCard title="Plan and access">
          {data ? (
            <dl className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Effective plan</dt>
                <dd className="font-medium">{data.plan.name}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Entitlement</dt>
                <dd>
                  <Badge variant={presentEntitlementStatus(data.plan.entitlement_status).variant}>
                    {presentEntitlementStatus(data.plan.entitlement_status).label}
                  </Badge>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Subscription</dt>
                <dd>
                  <Badge variant={presentSubscriptionStatus(data.billing.subscription_status).variant}>
                    {presentSubscriptionStatus(data.billing.subscription_status).label}
                  </Badge>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Billing interval</dt>
                <dd className="font-medium capitalize">{data.plan.billing_interval}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Quota resets</dt>
                <dd className="flex items-center gap-1 font-medium">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatDate(data.usage.window_end)}
                </dd>
              </div>
            </dl>
          ) : (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          )}
        </StatusCard>

        <StatusCard title="Account">
          <dl className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Name</dt>
              <dd className="font-medium">{account.account_name}</dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={presentAccountStatus(account.account_status).variant}>
                  {presentAccountStatus(account.account_status).label}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-medium">{formatDate(account.created_at)}</dd>
            </div>
          </dl>
        </StatusCard>
      </div>

      {/* Getting-started progress (Account ready authoritative). */}
      <div className="mt-4">
        <GettingStarted accountReady={true} activeApiKeyCount={data?.api_keys.active_count ?? 0} />
      </div>

      {/* Recent activity summaries — empty states (overview has no recent-jobs
          list; APP-002 §18: purposeful empty, no extra expensive calls). */}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Key className="h-4 w-4 text-muted-foreground" />
              API keys
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!data && loading ? (
              <Skeleton className="h-16 w-full" />
            ) : data && data.api_keys.active_count === 0 ? (
              <NoApiKeysEmpty />
            ) : (
              <p className="text-sm text-muted-foreground">
                {data ? `${data.api_keys.active_count} active key${data.api_keys.active_count === 1 ? '' : 's'}.` : '—'}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              Bulk jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <NoBulkJobsEmpty bulkSupported={bulkSupported} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Key className="h-4 w-4 text-muted-foreground" />
              Webhooks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <NoWebhooksEmpty webhooksSupported={bulkSupported} />
          </CardContent>
        </Card>
      </div>

      {/* Recommended next action. */}
      <div className="mt-4">
        <ActionCard
          title="Next step"
          description="Create an API key to start integrating the Signup Risk API into your application."
          icon={<Key className="h-5 w-5" />}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                asChild
                onClick={() => track({ event: 'dashboard_action_selected', action: 'create_api_key' })}
              >
                <Link to="/api-keys">
                  Create an API key
                </Link>
              </Button>
              <Button
                size="sm"
                variant="outline"
                asChild
                onClick={() => track({ event: 'dashboard_action_selected', action: 'try_playground' })}
              >
                <Link to="/playground">Try the playground</Link>
              </Button>
            </div>
          }
        />
      </div>
    </PageContainer>
  );
}
